import { useCallback, useRef, useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { getLocation } from "../graphql/queries";
import {
  getTrackCarForTrackManagement,
  trackCarByActiveByTrackBySortKeyMini,
} from "../graphql/trackCar/trackCar-queries";
import { UNSPOTTED_TRACK_ID } from "./constants";
import {
  mapTrackCarsByTrack,
  resolveTrackCarTrackId,
} from "./trackCarPersistence";
import {
  sortTrackCars,
  updateTracksWithCapacity,
  VIRTUAL_UNSPOTTED_TRACK,
  removeTrackCarFromMap,
  removeTrackCarByShipmentId,
} from "./trackListUtils";
import { logError, retryWithBackoff } from "./appUtils";
import { useDeferredActions } from "./useDeferredActions";
// Extracted hooks for better separation of concerns
import { useTrackFetcher } from "./useTrackFetcher";
import { useTrackSubscriptions } from "./useTrackSubscriptions";

/** First tracks to load for progressive UI; matches default manager grid width (4 columns). */
const PRIORITY_TRACK_COUNT = 4;
/** Manager/customer: max parallel per-track TrackCar queries (avoids client/API pile-up). */
const MANAGER_TRACK_FETCH_LIMIT = 12;

const isLeanTrackCarPayload = (trackCar) =>
  !!trackCar?.shipmentId && !trackCar?.inbound && !trackCar?.outbound;

export const buildQueuedTrackCarUpsert = ({ trackCar, existingCar }) => {
  let mergedTrackCar = trackCar;

  if (isLeanTrackCarPayload(trackCar) && existingCar) {
    mergedTrackCar = { ...existingCar, ...trackCar };
  }

  const resolvedTrackId = resolveTrackCarTrackId(mergedTrackCar);
  const cachedLocationId = mergedTrackCar?.location?.id;
  const locationIsStale =
    !!cachedLocationId && cachedLocationId !== resolvedTrackId;
  const previousTrackId = existingCar?.trackId ?? "";
  const existingCarIsLean =
    existingCar && !existingCar.inbound && !existingCar.outbound;
  const shouldHydrate =
    isLeanTrackCarPayload(trackCar) &&
    !!mergedTrackCar?.id &&
    (!existingCar || existingCarIsLean || previousTrackId !== resolvedTrackId);

  return {
    nextTrackCar: mergedTrackCar?.id
      ? {
          ...mergedTrackCar,
          trackId: resolvedTrackId,
          ...(locationIsStale ? { location: null } : null),
        }
      : null,
    previousTrackId,
    resolvedTrackId,
    // Always apply the lean merge optimistically: the payload's `trackId` is
    // authoritative for placement, and we've already nulled any stale `location`
    // above so downstream consumers can't read an inconsistent (trackId,
    // location.id) pair. Cross-track moves additionally trigger `shouldHydrate`
    // to backfill the `location` object (and other lean-omitted fields) via
    // `getTrackCar`. Older revisits of this code traded this snappiness for
    // flicker-resistance against late mini events; the hydration-token system
    // (see `shouldApplyTrackCarHydration`) now provides that resistance via
    // self-correction on the next hydrate response, so we get both.
    shouldApplyImmediate: true,
    shouldHydrate,
  };
};

export const shouldApplyTrackCarHydration = ({
  trackCarId,
  hydrationToken,
  latestHydrationTokens,
}) => {
  if (!trackCarId || hydrationToken == null) return true;
  return latestHydrationTokens?.get(trackCarId) === hydrationToken;
};

export const buildHydratedTrackCarForMap = ({ fetchedTrackCar }) => {
  const trackId = resolveTrackCarTrackId(fetchedTrackCar);
  if (!trackId) return null;

  return {
    ...fetchedTrackCar,
    trackId,
  };
};

export const setProcessedCarsMapAndSyncRef = ({
  payload,
  setProcessedCarsMap,
  processedCarsMapRef,
}) => {
  const syncRef = (nextMap) => {
    if (
      typeof nextMap === "object" &&
      nextMap !== null &&
      !Array.isArray(nextMap)
    ) {
      processedCarsMapRef.current = nextMap;
    }
  };

  if (typeof payload === "function") {
    setProcessedCarsMap((prev) => {
      const nextMap = payload(prev);
      syncRef(nextMap);
      return nextMap;
    });
    return;
  }

  syncRef(payload);
  setProcessedCarsMap(payload);
};

/**
 * Locate a TrackCar in the processed cars map by id (preferred) or shipmentId
 * (fallback for lean subscription payloads that may arrive without the canonical
 * TrackCar id). Pure: doesn't read or mutate any external state.
 */
export const findCarInMap = (map, trackCarId, shipmentId) => {
  if (!map) return { car: null, trackId: "" };
  for (const trackId in map) {
    const found = map[trackId]?.find(
      (c) =>
        (trackCarId && c.id === trackCarId) ||
        (shipmentId && c.shipmentId === shipmentId),
    );
    if (found) return { car: found, trackId };
  }
  return { car: null, trackId: "" };
};

/**
 * Pure reducer for `processQueuedUpdates`. Given the current `processedCarsMap`
 * and a batch of queued subscription/optimistic actions, compute the next map
 * plus all derived intents (capacity refreshes, lean hydrations, hydration-token
 * bookkeeping). Side effects — token-ref writes, debounced refresh kicks,
 * `hydrateLeanTrackCars`, etc. — are deferred to the caller so this function is
 * safe to invoke twice in React 18 StrictMode without duplicating work or
 * desynchronising the global hydration counter.
 *
 * @param {Object} params
 * @param {Array} params.queuedActions
 * @param {Object} params.prevMap          Current `processedCarsMap`.
 * @param {Map}    params.latestHydrationTokens
 *   Snapshot of `latestHydrationTokenByTrackCarIdRef.current`. Read-only.
 * @param {number} params.nextHydrationTokenStart
 *   Current value of `nextHydrationTokenRef.current`. Tokens are minted by
 *   incrementing a local copy; the caller is responsible for committing the
 *   final value back to the ref exactly once.
 * @returns {Object} plan
 */
export const planQueuedUpdates = ({
  queuedActions = [],
  prevMap = {},
  // eslint-disable-next-line no-unused-vars -- reserved for future stale-hydration logic
  latestHydrationTokens,
  nextHydrationTokenStart = 0,
}) => {
  const affectedTrackIds = new Set();
  const locationsToUpdate = [];
  const leanUpserts = [];
  const tokenWrites = new Map();
  const tokenDeletes = new Set();
  let needsImmediateRefresh = false;
  let nextHydrationToken = nextHydrationTokenStart;

  // Phase 1: derive affected tracks and surface obvious errors. Mirrors the
  // pre-pass in the original imperative `processQueuedUpdates` so the debounced
  // capacity refresh kicks even when an action is later rejected for missing
  // ids.
  queuedActions.forEach((action) => {
    const trackCar = action.data;
    if (!trackCar?.id && !trackCar?.shipmentId) {
      needsImmediateRefresh = true;
      return;
    }
    if (trackCar?.location) locationsToUpdate.push(trackCar.location);

    const tid = resolveTrackCarTrackId(trackCar);
    if (!tid) {
      if (action.type === "delete") {
        // Removal payloads (active="false" / onDeleteTrackCar) carry
        // trackId:"" by design. Recover the source track from the current map
        // so its capacity refreshes via the debounced path instead of a full
        // refetch. Fall through to the full refresh only when the car is
        // genuinely unknown.
        const { trackId: prevTid } = findCarInMap(
          prevMap,
          trackCar.id,
          trackCar.shipmentId,
        );
        if (prevTid) {
          affectedTrackIds.add(prevTid);
          return;
        }
      } else {
        logError(
          "TrackManagement:processQueuedUpdates:missingTrackId",
          { trackCar },
          null,
        );
      }
      needsImmediateRefresh = true;
      return;
    }
    affectedTrackIds.add(tid);
  });

  // Phase 2: fold each action into a working map copy.
  let workingMap = { ...prevMap };
  queuedActions.forEach((action) => {
    const tc = action.data;

    if (action.type === "delete") {
      if (!tc?.id && !tc?.shipmentId) {
        needsImmediateRefresh = true;
        return;
      }
      workingMap = removeTrackCarFromMap(workingMap, tc.id);
      workingMap = removeTrackCarByShipmentId(workingMap, tc.shipmentId);
      // Cancel any earlier token write in this same batch and instruct the
      // caller to free the ref-map slot. An in-flight `getTrackCar` whose token
      // was minted before this delete will see `map.get(id) === undefined`,
      // miss its captured token, and be rejected — preventing resurrection.
      if (tc?.id != null) {
        tokenWrites.delete(tc.id);
        tokenDeletes.add(tc.id);
      }
      // In crew flow, unspotted placement must come from canonical upsert
      // payloads (active=true with trackId=UNSPOTTED_TRACK_ID), never from
      // generic delete events.
      return;
    }

    if (action.type !== "upsert") return;
    if (!tc?.id && !tc?.shipmentId) {
      needsImmediateRefresh = true;
      return;
    }

    const hydrationToken = tc?.id != null ? ++nextHydrationToken : null;
    if (tc?.id != null) {
      tokenWrites.set(tc.id, hydrationToken);
      // Cancel any earlier delete intent in this same batch — a later upsert
      // for the same id wins.
      tokenDeletes.delete(tc.id);
    }

    let existingCar = null;
    if (!tc?.inbound && !tc?.outbound && tc?.shipmentId) {
      ({ car: existingCar } = findCarInMap(workingMap, tc.id, tc.shipmentId));
    }

    const {
      nextTrackCar,
      previousTrackId,
      resolvedTrackId,
      shouldApplyImmediate,
      shouldHydrate,
    } = buildQueuedTrackCarUpsert({ trackCar: tc, existingCar });

    if (!nextTrackCar?.id) {
      if (tc?.id) {
        leanUpserts.push({ ...tc, hydrationToken });
      }
      return;
    }

    if (!resolvedTrackId) {
      logError(
        "TrackManagement:processQueuedUpdates:upsertMissingTrackId",
        { tc },
        null,
      );
      needsImmediateRefresh = true;
      return;
    }

    if (shouldHydrate) {
      leanUpserts.push({ ...tc, hydrationToken, previousTrackId });
      // Lean subscription updates omit nested shipment/product fields. Hydrate
      // when the current map cannot already supply a full TrackCar shape, and
      // refresh the source capacity too when that source is known.
      // The optimistic placement below is the user-visible move; hydration
      // arrives later as a silent backfill.
      if (previousTrackId) affectedTrackIds.add(previousTrackId);
    }

    // Reserved escape hatch — `buildQueuedTrackCarUpsert` currently always
    // returns `shouldApplyImmediate: true` (see comment there). Kept so a
    // future "hydrate-then-commit" mode can be re-enabled without touching
    // this branch.
    if (!shouldApplyImmediate) return;

    workingMap = removeTrackCarFromMap(workingMap, nextTrackCar.id);
    workingMap = removeTrackCarByShipmentId(
      workingMap,
      nextTrackCar.shipmentId || nextTrackCar.id,
    );
    const currentList = workingMap[nextTrackCar.trackId] || [];
    workingMap[nextTrackCar.trackId] = sortTrackCars([
      ...currentList,
      nextTrackCar,
    ]);
  });

  return {
    nextMap: workingMap,
    affectedTrackIds,
    locationsToUpdate,
    leanUpserts,
    needsImmediateRefresh,
    tokenWrites,
    tokenDeletes,
    nextHydrationTokenEnd: nextHydrationToken,
  };
};

/** `task` is `() => Promise`. Queued tasks start as slots free (priority + tail can overlap). */
const limitConcurrency = (max) => {
  let running = 0;
  const q = [];
  const pump = () => {
    if (running >= max || q.length === 0) return;
    const { task, resolve, reject } = q.shift();
    running++;
    Promise.resolve()
      .then(task)
      .then(resolve, reject)
      .finally(() => {
        running--;
        pump();
      });
    pump();
  };
  return (task) =>
    new Promise((resolve, reject) => {
      q.push({ task, resolve, reject });
      pump();
    });
};

/** Crew: default selected track (inventory aisle or first). */
const applyCrewInitialTrackSelection = ({
  tracksWithVirtual,
  setSelectedTrack,
  selectedInventoryItem,
}) => {
  if (!setSelectedTrack) return;
  const aisleId = selectedInventoryItem?.aisleId;
  const match = aisleId
    ? tracksWithVirtual.find((t) => t.id === aisleId)
    : null;
  setSelectedTrack(match ?? tracksWithVirtual[0]);
};

/**
 * Shared hook for managing track data and real-time subscriptions.
 *
 * This is the main orchestration hook for Track Management, centralizing:
 * - Data fetching via useTrackFetcher (tracks, track cars, shipments)
 * - Real-time updates via useTrackSubscriptions (GraphQL subscriptions)
 * - Capacity management and refresh logic
 * - Deferred action processing during drag operations
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.setTracks - Setter for tracks array state
 * @param {Function} options.setProcessedCarsMap - Setter for processed cars map (trackId -> cars[])
 * @param {Function} options.setStaleTracks - Setter for stale tracks map (trackId -> error)
 * @param {Function} options.setLoading - Setter for loading state
 * @param {Array} options.tracks - Current tracks array (for refresh logic)
 * @param {Function} [options.setSelectedTrack] - Optional setter for selected track (Crew App)
 * @param {Function} [options.setSelectedShipment] - Optional setter for selected shipment (Crew App)
 * @param {Function} [options.notify] - Optional react-admin notify function
 * @param {string} options.reloadYardId - Current reload yard ID
 * @param {boolean} [options.isCrewApp=false] - Whether running in Crew App context
 * @param {boolean} [options.useSnapshot=false] - Use EOD snapshot mode for historical data
 * @param {string} [options.snapshotTimeZone=null] - Timezone for snapshot date calculations
 * @param {Object} [options.selectedInventoryItem=null] - Selected inventory item (Crew App)
 * @param {Object} [options.currentShipment=null] - Current shipment context (Crew App)
 * @param {string} [options.defaultShipmentId=null] - Default shipment to select (Crew App)
 * @param {Object} [options.isDraggingRef] - Ref tracking drag state (defers updates during drag)
 * @param {Object} [options.handleDragEndFlushRef] - Ref for flush callback on drag end
 *
 * @returns {Object} Hook API
 * @returns {Function} returns.init - getTrackCars once (layout + cars; no duplicate layout fetch)
 * @returns {Function} returns.getTracks - Fetch tracks only (async)
 * @returns {Function} returns.getTrackCars - Fetch track cars with options (async)
 * @returns {Function} returns.refreshTracksDebounced - Debounced capacity refresh
 * @returns {Function} returns.refreshSpecificTracks - Refresh specific track capacities
 * @returns {Object} returns.isCapacityOnlyUpdateRef - Ref indicating capacity-only update
 * @returns {Object} returns.justCompletedDragRef - Ref indicating recent drag completion
 *
 * @example
 * const {
 *   init,
 *   getTrackCars,
 *   refreshTracksDebounced,
 * } = useTrackManagement({
 *   setTracks,
 *   setProcessedCarsMap,
 *   setStaleTracks,
 *   setLoading,
 *   tracks,
 *   reloadYardId,
 *   isCrewApp: true,
 * });
 *
 * // Initialize on mount
 * useEffect(() => { init(); }, [init]);
 */
export const useTrackManagement = ({
  // State Setters
  setTracks,
  setProcessedCarsMap,
  setStaleTracks,
  setLoading,
  tracks, // Add this to keep track of current state for refreshes
  setSelectedTrack, // Optional (Crew App)
  setSelectedShipment, // Optional (Crew App)
  // Context
  notify,
  reloadYardId,
  // Configuration
  isCrewApp = false,
  useSnapshot = false,
  snapshotTimeZone = null,
  // Selection Context (Crew App)
  selectedInventoryItem = null,
  currentShipment = null,
  defaultShipmentId = null,
  // Refs for coordination
  isDraggingRef = { current: false },
  handleDragEndFlushRef = null,
}) => {
  const fetchingPromiseRef = useRef(null);
  const isMountedRef = useRef(true);
  const processedCarsMapRef = useRef({});
  const refreshTimeoutRef = useRef(null);
  const pendingTrackIdsRef = useRef(new Set());
  const secondaryRefreshTimeoutRef = useRef(null);
  /** Delayed capacity refresh so UI picks up lambda-written Location.capacityUsed (lambda runs async after stream). */
  const capacityRefreshTimeoutRef = useRef(null);
  const justCompletedDragRef = useRef(false);
  const isCapacityOnlyUpdateRef = useRef(false);
  const [subscriptionsEpoch, setSubscriptionsEpoch] = useState(0);
  const reloadYardIdRef = useRef(reloadYardId);
  const selectedInventoryItemRef = useRef(selectedInventoryItem);
  const lastSubscriptionEventRef = useRef(Date.now());
  // Fetch tokens ensure only the latest in-flight request can clear refs or trigger pending refreshes.
  const fetchTokenRef = useRef(0);
  const fetchingPromiseTokenRef = useRef(0);
  const fetchingPromiseYardIdRef = useRef(null);
  const latestHydrationTokenByTrackCarIdRef = useRef(new Map());
  const nextHydrationTokenRef = useRef(0);

  const visibleTrackIdsRef = useRef(null);
  const pendingVisibleTrackIdsRef = useRef(null);
  const pendingResetCrewInitialSelectionRef = useRef(false);
  const pendingFetchRef = useRef(false);

  const subscriptionsDisabledRef = useRef(false);
  const subscriptionsRef = useRef([]);

  // Use extracted fetcher hook - provides fetchTracksForYard and fetchTrackCars
  // Note: getSnapshotDatePrefix is used internally by the fetchers and not needed here
  const { fetchTracksForYard, fetchTrackCars } = useTrackFetcher({
    reloadYardId,
    useSnapshot,
    snapshotTimeZone,
  });

  useEffect(() => {
    isMountedRef.current = true;
    const refreshTimeout = refreshTimeoutRef;
    const secondaryRefreshTimeout = secondaryRefreshTimeoutRef;
    const capacityRefreshTimeout = capacityRefreshTimeoutRef;

    return () => {
      isMountedRef.current = false;
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
      if (secondaryRefreshTimeout.current)
        clearTimeout(secondaryRefreshTimeout.current);
      if (capacityRefreshTimeout.current)
        clearTimeout(capacityRefreshTimeout.current);
    };
  }, []);

  useEffect(() => {
    reloadYardIdRef.current = reloadYardId;
  }, [reloadYardId]);

  useEffect(() => {
    selectedInventoryItemRef.current = selectedInventoryItem;
  }, [selectedInventoryItem]);

  const refreshSpecificTracks = useCallback(
    async (trackLocationIds) => {
      if (!trackLocationIds?.length) return;

      try {
        const locationPromises = trackLocationIds.map(async (id) => {
          try {
            const response = await API.graphql(
              graphqlOperation(getLocation, { id }),
            );
            return response?.data?.getLocation || null;
          } catch (err) {
            logError(
              `TrackManagement:refreshSpecificTracks:${id}`,
              err,
              notify,
            );
            return null;
          }
        });

        const updatedLocations = (await Promise.all(locationPromises)).filter(
          Boolean,
        );
        if (updatedLocations.length === 0) return;

        isCapacityOnlyUpdateRef.current = true;
        setTracks((prev) => {
          if (!prev?.length) return prev;
          return updateTracksWithCapacity(prev, updatedLocations);
        });

        setTimeout(() => {
          isCapacityOnlyUpdateRef.current = false;
        }, 500);
      } catch (err) {
        logError("TrackManagement:refreshSpecificTracks", err, notify);
      }
    },
    [notify, setTracks],
  );

  const refreshTracksDebounced = useCallback(
    (trackLocationIds = null) => {
      if (trackLocationIds === null) {
        pendingTrackIdsRef.current.clear();
      } else if (Array.isArray(trackLocationIds)) {
        trackLocationIds.forEach(
          (id) => id && pendingTrackIdsRef.current.add(id),
        );
      }

      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => {
        const idsToRefresh = Array.from(pendingTrackIdsRef.current);
        pendingTrackIdsRef.current.clear();
        refreshTimeoutRef.current = null;

        if (idsToRefresh.length > 0) {
          refreshSpecificTracks(idsToRefresh);
          // Lambda updates Location.capacityUsed via DynamoDB after TrackCar stream; no AppSync event.
          // Delayed refreshes so other browsers pick up capacity (Lambda runs async after stream).
          if (capacityRefreshTimeoutRef.current)
            clearTimeout(capacityRefreshTimeoutRef.current);
          const CAPACITY_REFRESH_DELAY_MS = 4500;
          const CAPACITY_REFRESH_SECOND_DELAY_MS = 6500;
          capacityRefreshTimeoutRef.current = setTimeout(() => {
            refreshSpecificTracks(idsToRefresh);
            capacityRefreshTimeoutRef.current = setTimeout(() => {
              capacityRefreshTimeoutRef.current = null;
              refreshSpecificTracks(idsToRefresh);
            }, CAPACITY_REFRESH_SECOND_DELAY_MS - CAPACITY_REFRESH_DELAY_MS);
          }, CAPACITY_REFRESH_DELAY_MS);
        }
      }, 250);
    },
    [refreshSpecificTracks],
  );

  const fetchTrackCarsByIds = useCallback(
    async (trackCarIds = []) => {
      const uniqueIds = Array.from(
        new Set((trackCarIds || []).filter(Boolean)),
      );
      if (!uniqueIds.length) return [];

      const results = await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const res = await retryWithBackoff(
              () =>
                API.graphql(
                  graphqlOperation(getTrackCarForTrackManagement, { id }),
                ),
              { maxRetries: 2 },
            );
            return res?.data?.getTrackCar || null;
          } catch (err) {
            logError(`TrackManagement:fetchTrackCar:${id}`, err, notify);
            return null;
          }
        }),
      );

      return results.filter(Boolean);
    },
    [notify],
  );

  // Main fetch function
  const getTrackCars = useCallback(
    async ({
      showSpinner = true,
      skipLoading = false,
      visibleTrackIds = null,
      resetCrewInitialSelection = false,
    } = {}) => {
      const fetchYardId = reloadYardId;
      const isStaleFetch = () => reloadYardIdRef.current !== fetchYardId;
      const normalizedVisibleTrackIds = Array.isArray(visibleTrackIds)
        ? visibleTrackIds.length > 0
          ? visibleTrackIds
          : null
        : undefined;

      // Track visible track IDs even if a fetch is already in-flight.
      if (normalizedVisibleTrackIds !== undefined) {
        visibleTrackIdsRef.current = normalizedVisibleTrackIds;
      }

      if (
        fetchingPromiseRef.current &&
        fetchingPromiseYardIdRef.current === fetchYardId
      ) {
        if (normalizedVisibleTrackIds !== undefined) {
          pendingVisibleTrackIdsRef.current = normalizedVisibleTrackIds;
          pendingResetCrewInitialSelectionRef.current =
            resetCrewInitialSelection;
          pendingFetchRef.current = true;
        }
        return fetchingPromiseRef.current;
      }

      const fetchToken = fetchTokenRef.current + 1;
      fetchTokenRef.current = fetchToken;
      fetchingPromiseTokenRef.current = fetchToken;
      fetchingPromiseYardIdRef.current = fetchYardId;
      const isLatestFetch = () =>
        fetchingPromiseTokenRef.current === fetchToken;

      const fetchInternal = async () => {
        const fetchStartedAt = Date.now();
        try {
          if (isMountedRef.current && (showSpinner || !skipLoading)) {
            setLoading(true);
          }

          if (!fetchYardId) {
            if (isMountedRef.current) {
              setProcessedCarsMap({});
              if (notify) notify("Reload yard selection required.", "warning");
              setLoading(false);
            }
            return;
          }

          let allActiveShipments = [];
          let unspottedTrackCars = [];

          // Fetch track layout first, then fetch TrackCars by strategy:
          // - Crew: targeted visible tracks + unspotted lane
          // - Manager/Customer (default): queue per-track fetches (capped concurrency),
          //   await first PRIORITY_TRACK_COUNT for progressive UI, then await the tail (same promises)
          const trackFetchPromise = fetchTracksForYard(fetchYardId);

          // Whether to load the Unspotted lane. Crew and manager show the
          // Unspotted bucket; the customer portal loads it but hides it, so a
          // car spotted out of it lands on its track right away. Snapshot (EOD)
          // mode skips it.
          const includeUnspotted = isCrewApp || !useSnapshot;

          // Yard-wide fetch of unspotted TrackCars (active=true, trackId=UNSPOTTED).
          // Shared by crew (targeted branch below) and manager (default branch);
          // paginated up to 1000/page.
          const fetchUnspottedTrackCars = async () => {
            const items = [];
            let nextToken = null;
            const baseInput = {
              reloadYardId,
              activeTrackIdSortKey: {
                beginsWith: { active: "true", trackId: UNSPOTTED_TRACK_ID },
              },
              limit: 1000,
            };

            do {
              const result = await API.graphql(
                graphqlOperation(trackCarByActiveByTrackBySortKeyMini, {
                  ...baseInput,
                  nextToken,
                }),
              );
              const page = result?.data?.trackCarByActiveByTrackBySortKey;
              if (page?.items?.length) {
                items.push(...page.items);
              }
              nextToken = page?.nextToken ?? null;
            } while (nextToken);

            return items;
          };

          // Manager default path: kick off the unspotted fetch in parallel with
          // the per-track fetches; awaited after the per-track loop below.
          // (Crew fetches it inside its targeted branch; customer skips it.)
          const managerUnspottedPromise =
            includeUnspotted && !isCrewApp
              ? fetchUnspottedTrackCars().catch((err) => {
                  logError(
                    "TrackManagement:getTrackCars:unspottedFetch",
                    err,
                    notify,
                  );
                  return [];
                })
              : null;

          let tracksFromApi = [];
          tracksFromApi = await trackFetchPromise;

          if (isStaleFetch()) return;

          // Update tracks state early to allow UI to render headers
          if (isMountedRef.current && !isStaleFetch()) {
            const tracksWithVirtual = isCrewApp
              ? [...tracksFromApi, VIRTUAL_UNSPOTTED_TRACK]
              : tracksFromApi.filter((t) => t.id !== UNSPOTTED_TRACK_ID);
            setTracks(tracksWithVirtual);
            if (isCrewApp && resetCrewInitialSelection) {
              applyCrewInitialTrackSelection({
                tracksWithVirtual,
                setSelectedTrack,
                selectedInventoryItem: selectedInventoryItemRef.current,
              });
            }
          }

          const trackLocationIds = tracksFromApi
            .map((aisle) => aisle?.locations?.items?.[0]?.id)
            .filter(Boolean);

          // If caller did not specify visible tracks, use tracked ref or default
          const currentVisibleIds =
            visibleTrackIds !== null
              ? visibleTrackIds
              : visibleTrackIdsRef.current;
          const targetTracks =
            currentVisibleIds && currentVisibleIds.length > 0
              ? currentVisibleIds
              : isCrewApp
                ? trackLocationIds.slice(0, 2)
                : trackLocationIds;

          let trackCarItems = [];
          let nextStaleTracks = {};
          let usedYardWide = false;
          let priorityTrackCars = [];
          let priorityTrackIds = [];
          let shouldReplaceTrackMap = false;
          let remainingTrackIds = trackLocationIds;

          // Targeted track fetch + unspotted cars; need rest of yard for unspotted identification.
          if (isCrewApp && targetTracks && targetTracks.length > 0) {
            // Crew portal only: targeted track fetch + unspotted TrackCars
            try {
              const [visibleCarsResults, unspottedCars] = await Promise.all([
                Promise.all(targetTracks.map((tid) => fetchTrackCars(tid))),
                fetchUnspottedTrackCars(),
              ]);

              trackCarItems = visibleCarsResults.flat();
              unspottedTrackCars = unspottedCars;
              usedYardWide = true; // Prevents the next block from running
            } catch (err) {
              logError("TrackManagement:getTrackCars:targeted", err, notify);
            }
          }

          if (!usedYardWide) {
            const runTrackCarFetch = async (trackId) => {
              try {
                const cars = await retryWithBackoff(
                  () => fetchTrackCars(trackId),
                  { maxRetries: 3 },
                );
                return { trackId, cars, success: true };
              } catch (err) {
                logError(
                  `TrackManagement:getTrackCars:trackId:${trackId}`,
                  err,
                  notify,
                );
                return { trackId, cars: [], success: false, error: err };
              }
            };

            const managerEnqueue = isCrewApp
              ? null
              : limitConcurrency(MANAGER_TRACK_FETCH_LIMIT);
            const scheduleTrackFetch = (trackId) =>
              managerEnqueue
                ? managerEnqueue(() => runTrackCarFetch(trackId))
                : runTrackCarFetch(trackId);

            let tailTrackCarPromises = null;

            if (!isCrewApp) {
              const shouldPrioritizeVisibleTracksFirst =
                normalizedVisibleTrackIds === null ||
                normalizedVisibleTrackIds === undefined;
              shouldReplaceTrackMap = shouldPrioritizeVisibleTracksFirst;

              if (!shouldPrioritizeVisibleTracksFirst) {
                const availableTrackIds = new Set(trackLocationIds);
                remainingTrackIds = Array.from(new Set(targetTracks)).filter(
                  (trackId) => availableTrackIds.has(trackId),
                );
              }

              if (shouldPrioritizeVisibleTracksFirst) {
                priorityTrackIds = trackLocationIds.slice(
                  0,
                  PRIORITY_TRACK_COUNT,
                );

                if (priorityTrackIds.length > 0) {
                  const allTrackCarPromises = trackLocationIds.map((trackId) =>
                    scheduleTrackFetch(trackId),
                  );
                  try {
                    const priorityResults = await Promise.all(
                      allTrackCarPromises.slice(0, priorityTrackIds.length),
                    );

                    if (isStaleFetch()) return;

                    priorityTrackCars = priorityResults.flatMap((res) =>
                      res.success ? res.cars : [],
                    );
                    trackCarItems = [...priorityTrackCars];
                    const {
                      byTrackId: priorityTrackMap,
                      placedShipmentIds: priorityPlacedShipmentIds,
                    } = mapTrackCarsByTrack(priorityTrackCars);

                    priorityResults.forEach((res) => {
                      if (!res.success) {
                        nextStaleTracks[res.trackId] =
                          res.error?.message || "Failed to fetch";
                      }
                    });

                    priorityTrackIds.forEach((trackId) => {
                      if (!priorityTrackMap[trackId]) {
                        priorityTrackMap[trackId] = [];
                      }
                    });

                    if (isMountedRef.current && !isStaleFetch()) {
                      // Fold the unspotted lane into the very first commit so the
                      // Unspotted bucket paints with the priority tracks instead of
                      // flashing empty until the tail/lane-build completes. Both
                      // queries were started in parallel above, so this awaits
                      // max(priority tracks, one indexed unspotted query) — no
                      // added serial latency. (line 901 re-await is a no-op.)
                      let priorityMerge = priorityTrackMap;
                      if (managerUnspottedPromise) {
                        unspottedTrackCars = await managerUnspottedPromise;
                        // Same shipment-level dedup as the authoritative lane
                        // build below: don't paint a railcar in Unspotted if a
                        // stale duplicate is already spotted on a priority track.
                        const prioritySeenShipmentIds = new Set(
                          priorityPlacedShipmentIds,
                        );
                        priorityMerge = {
                          ...priorityTrackMap,
                          [UNSPOTTED_TRACK_ID]: sortTrackCars(
                            (unspottedTrackCars || [])
                              .filter(Boolean)
                              .filter((tc) => {
                                if (
                                  tc.shipmentId &&
                                  prioritySeenShipmentIds.has(tc.shipmentId)
                                )
                                  return false;
                                if (tc.shipmentId)
                                  prioritySeenShipmentIds.add(tc.shipmentId);
                                return true;
                              })
                              .map((tc) => ({
                                ...tc,
                                trackId: UNSPOTTED_TRACK_ID,
                              })),
                          ),
                        };
                      }
                      setProcessedCarsMap((prev) => {
                        const merged = { ...(prev || {}), ...priorityMerge };
                        processedCarsMapRef.current = merged;
                        return merged;
                      });
                    }

                    remainingTrackIds =
                      trackLocationIds.slice(PRIORITY_TRACK_COUNT);
                    tailTrackCarPromises = allTrackCarPromises.slice(
                      priorityTrackIds.length,
                    );
                  } catch (err) {
                    logError(
                      "TrackManagement:getTrackCars:priorityTracks",
                      err,
                      notify,
                    );
                    tailTrackCarPromises = allTrackCarPromises;
                    trackCarItems = [];
                  }
                }
              }
            }

            const tailResults = await Promise.all(
              tailTrackCarPromises?.length
                ? tailTrackCarPromises
                : remainingTrackIds.map((trackId) =>
                    scheduleTrackFetch(trackId),
                  ),
            );

            tailResults.forEach((res) => {
              if (res.success) {
                trackCarItems.push(...res.cars);
              } else {
                nextStaleTracks[res.trackId] =
                  res.error?.message || "Failed to fetch";
              }
            });
          }

          // Manager default path: fold in the yard-wide unspotted fetch result
          // (crew already populated unspottedTrackCars in its targeted branch).
          if (managerUnspottedPromise) {
            unspottedTrackCars = await managerUnspottedPromise;
          }

          if (isCrewApp) {
            allActiveShipments = [
              ...trackCarItems,
              ...(unspottedTrackCars || []),
            ];
          }

          // Build final map in a single pass
          const { byTrackId, placedShipmentIds } =
            mapTrackCarsByTrack(trackCarItems);
          const nextProcessedCarsMap = byTrackId;

          // Ensure fetched tracks are represented, even if empty.
          // Crew always needs this for targeted fetches; manager/customer needs this for filtered fetches.
          const isFilteredManagerFetch =
            !isCrewApp &&
            normalizedVisibleTrackIds !== null &&
            normalizedVisibleTrackIds !== undefined;
          if (
            (isCrewApp || isFilteredManagerFetch) &&
            Array.isArray(targetTracks) &&
            targetTracks.length
          ) {
            targetTracks.forEach((trackId) => {
              if (!nextProcessedCarsMap[trackId]) {
                nextProcessedCarsMap[trackId] = [];
              }
            });
          }

          // Build Unspotted lane for any app that shows it (crew + manager).
          if (includeUnspotted) {
            const unspottedCars =
              nextProcessedCarsMap[UNSPOTTED_TRACK_ID] || [];
            const seenUnspottedIds = new Set(unspottedCars.map((tc) => tc.id));
            // A shipment already placed on a physical track (or already in this
            // lane) must not also appear in Unspotted. The yard-wide unspotted
            // fetch bypasses mapTrackCarsByTrack's shipment dedup, so stale
            // duplicate active rows (one spotted, one UNSPOTTED) would otherwise
            // render the same railcar twice. Skip by shipmentId here too.
            const seenShipmentIds = new Set([
              ...placedShipmentIds,
              ...unspottedCars.map((tc) => tc.shipmentId).filter(Boolean),
            ]);
            const rawUnspotted = (unspottedTrackCars || []).filter(Boolean);

            for (let i = 0; i < rawUnspotted.length; i++) {
              const trackCar = rawUnspotted[i];
              if (!trackCar?.id || seenUnspottedIds.has(trackCar.id)) continue;
              if (
                trackCar.shipmentId &&
                seenShipmentIds.has(trackCar.shipmentId)
              )
                continue;
              unspottedCars.push({
                ...trackCar,
                trackId: UNSPOTTED_TRACK_ID,
              });
              seenUnspottedIds.add(trackCar.id);
              if (trackCar.shipmentId)
                seenShipmentIds.add(trackCar.shipmentId);
            }
            nextProcessedCarsMap[UNSPOTTED_TRACK_ID] =
              sortTrackCars(unspottedCars);
          }

          if (isMountedRef.current && !isStaleFetch()) {
            // Skip applying results from fetches that started before the last move completed.
            const lastMoveAt = justCompletedDragRef.current || 0;
            if (fetchStartedAt >= lastMoveAt) {
              const shouldReplaceAll =
                !isCrewApp &&
                shouldReplaceTrackMap &&
                (normalizedVisibleTrackIds === null ||
                  normalizedVisibleTrackIds === undefined);

              // Manager/Customer default load replaces map so empty tracks clear correctly.
              if (shouldReplaceAll && trackLocationIds.length > 0) {
                trackLocationIds.forEach((trackId) => {
                  if (!nextProcessedCarsMap[trackId]) {
                    nextProcessedCarsMap[trackId] = [];
                  }
                });
              }

              const mergedProcessedCarsMap = shouldReplaceAll
                ? nextProcessedCarsMap
                : {
                    ...(processedCarsMapRef.current || {}),
                    ...nextProcessedCarsMap,
                  };

              const allowedTrackIds = new Set(trackLocationIds);
              allowedTrackIds.add(UNSPOTTED_TRACK_ID);
              allowedTrackIds.add(VIRTUAL_UNSPOTTED_TRACK.id);

              const finalProcessedCarsMap =
                trackLocationIds.length > 0
                  ? Object.fromEntries(
                      Object.entries(mergedProcessedCarsMap).filter(([key]) =>
                        allowedTrackIds.has(key),
                      ),
                    )
                  : mergedProcessedCarsMap;
              setProcessedCarsMap(finalProcessedCarsMap);
              processedCarsMapRef.current = finalProcessedCarsMap;
            }
            setStaleTracks(nextStaleTracks);

            // App-specific selection logic (Crew App)
            if (isCrewApp && setSelectedShipment) {
              if (currentShipment) {
                setSelectedShipment(
                  allActiveShipments.find((s) => s.id === currentShipment.id),
                );
              } else if (defaultShipmentId) {
                setSelectedShipment(
                  allActiveShipments.find((s) => s.id === defaultShipmentId),
                );
              } else if (selectedInventoryItem) {
                const sid =
                  selectedInventoryItem.inboundShipmentId ||
                  selectedInventoryItem.outboundShipmentId;
                setSelectedShipment(
                  allActiveShipments.find((s) => s.id === sid),
                );
              }
            }
          }
        } catch (err) {
          logError("TrackManagement:getTrackCars", err, notify);
        } finally {
          if (isLatestFetch()) {
            if (isMountedRef.current && !isStaleFetch()) setLoading(false);
            fetchingPromiseRef.current = null;
            if (pendingFetchRef.current) {
              const pendingVisibleIds = pendingVisibleTrackIdsRef.current;
              const pendingResetCrewInitialSelection =
                pendingResetCrewInitialSelectionRef.current;
              pendingFetchRef.current = false;
              pendingVisibleTrackIdsRef.current = null;
              pendingResetCrewInitialSelectionRef.current = false;
              setTimeout(() => {
                if (!isMountedRef.current) return;
                getTrackCars({
                  showSpinner: false,
                  skipLoading: true,
                  visibleTrackIds: pendingVisibleIds,
                  resetCrewInitialSelection: pendingResetCrewInitialSelection,
                });
              }, 0);
            }
          }
        }
      };

      fetchingPromiseRef.current = fetchInternal();
      return fetchingPromiseRef.current;
    },
    [
      reloadYardId,
      fetchTrackCars,
      isCrewApp,
      useSnapshot,
      currentShipment,
      defaultShipmentId,
      selectedInventoryItem,
      setSelectedShipment,
      setSelectedTrack,
      setProcessedCarsMap,
      setStaleTracks,
      setLoading,
      notify,
      fetchTracksForYard,
      setTracks,
    ],
  );

  const getTracks = useCallback(
    async ({ showSpinner = true } = {}) => {
      try {
        if (showSpinner) setLoading(true);
        if (!reloadYardId) return;

        const tracksFromApi = await fetchTracksForYard(reloadYardId);
        const tracksWithVirtual = isCrewApp
          ? [...tracksFromApi, VIRTUAL_UNSPOTTED_TRACK]
          : tracksFromApi.filter((t) => t.id !== UNSPOTTED_TRACK_ID);

        if (isMountedRef.current) {
          setTracks(tracksWithVirtual);
          if (isCrewApp) {
            applyCrewInitialTrackSelection({
              tracksWithVirtual,
              setSelectedTrack,
              selectedInventoryItem: selectedInventoryItemRef.current,
            });
          }
        }
      } catch (err) {
        logError("TrackManagement:getTracks", err, notify);
      } finally {
        if (isMountedRef.current && showSpinner) setLoading(false);
      }
    },
    [
      reloadYardId,
      isCrewApp,
      setSelectedTrack,
      setTracks,
      setLoading,
      notify,
      fetchTracksForYard,
    ],
  );

  const init = useCallback(
    async ({ visibleTrackIds = null } = {}) => {
      await getTrackCars({
        showSpinner: true,
        visibleTrackIds,
        resetCrewInitialSelection: isCrewApp,
      });
    },
    [getTrackCars, isCrewApp],
  );

  // Subscriptions logic
  const hydrateLeanTrackCars = useCallback(
    async (leanTrackCars = []) => {
      try {
        const hydrationRequests = (leanTrackCars || []).filter((tc) => tc?.id);
        const fetched = await fetchTrackCarsByIds(
          hydrationRequests.map((tc) => tc.id),
        );

        // If we failed to hydrate, fall back to a lightweight refresh
        if (!fetched.length) {
          getTrackCars({ showSpinner: false, skipLoading: true });
          return;
        }

        const locationsToUpdate = [];
        const affectedTrackIds = new Set();

        const hydrationRequestMap = new Map(
          hydrationRequests.map((tc) => [tc.id, tc]),
        );

        setProcessedCarsMap((prev) => {
          let workingMap = { ...prev };

          fetched.forEach((tc) => {
            const hydrationRequest = hydrationRequestMap.get(tc.id);

            if (
              !shouldApplyTrackCarHydration({
                trackCarId: tc.id,
                hydrationToken: hydrationRequest?.hydrationToken,
                latestHydrationTokens:
                  latestHydrationTokenByTrackCarIdRef.current,
              })
            ) {
              return;
            }

            const mergedTc = buildHydratedTrackCarForMap({
              fetchedTrackCar: tc,
            });
            if (!mergedTc) {
              logError(
                "TrackManagement:hydrateLeanTrackCars:missingTrackId",
                { tc },
                null,
              );
              return;
            }

            if (tc.location) {
              locationsToUpdate.push(tc.location);
            }
            affectedTrackIds.add(mergedTc.trackId);
            if (hydrationRequest?.previousTrackId) {
              affectedTrackIds.add(hydrationRequest.previousTrackId);
            }

            workingMap = removeTrackCarFromMap(workingMap, mergedTc.id);
            workingMap = removeTrackCarByShipmentId(
              workingMap,
              tc.shipmentId || mergedTc.id,
            );
            const currentList = workingMap[mergedTc.trackId] || [];
            workingMap[mergedTc.trackId] = sortTrackCars([
              ...currentList,
              mergedTc,
            ]);
          });

          processedCarsMapRef.current = workingMap;
          return workingMap;
        });

        if (locationsToUpdate.length) {
          setTracks((prev) =>
            updateTracksWithCapacity(prev, locationsToUpdate),
          );
        }

        const trackIdsArray = Array.from(affectedTrackIds).filter(Boolean);
        if (trackIdsArray.length > 0) {
          refreshTracksDebounced(trackIdsArray);
        }
      } catch (err) {
        // Last-resort fallback to keep clients consistent
        getTrackCars({ showSpinner: false, skipLoading: true });
      }
    },
    [
      fetchTrackCarsByIds,
      refreshTracksDebounced,
      setProcessedCarsMap,
      setTracks,
      getTrackCars,
    ],
  );

  const processQueuedUpdates = useCallback(
    (queuedActions) => {
      // Plan first, mutate second. The planner is pure: it computes the next
      // map, lean-hydration intents, capacity-refresh targets, and hydration-
      // token bookkeeping without touching React state or refs. We then commit
      // those side effects exactly once below — making the whole flow safe
      // under React 18 StrictMode (no token-counter desync, no duplicated
      // lean-hydration enqueues).
      const plan = planQueuedUpdates({
        queuedActions,
        prevMap: processedCarsMapRef.current || {},
        latestHydrationTokens: latestHydrationTokenByTrackCarIdRef.current,
        nextHydrationTokenStart: nextHydrationTokenRef.current,
      });

      // Commit hydration-token bookkeeping once. Order matters: writes first,
      // then deletes — so an upsert-then-delete sequence in the same batch
      // ends with the slot freed and any in-flight hydrate rejected.
      nextHydrationTokenRef.current = plan.nextHydrationTokenEnd;
      plan.tokenWrites.forEach((token, id) => {
        latestHydrationTokenByTrackCarIdRef.current.set(id, token);
      });
      plan.tokenDeletes.forEach((id) => {
        latestHydrationTokenByTrackCarIdRef.current.delete(id);
      });

      // Update the canonical ref before the React commit so any back-to-back
      // flush (rAF-driven from `useDeferredActions`) sees the freshest map
      // even if React hasn't rendered yet.
      processedCarsMapRef.current = plan.nextMap;
      setProcessedCarsMap(plan.nextMap);

      if (plan.locationsToUpdate.length) {
        setTracks((prev) =>
          updateTracksWithCapacity(prev, plan.locationsToUpdate),
        );
      }

      // Debounced capacity refresh only; avoid immediate refreshSpecificTracks
      // so we don't double-fetch GetLocation for every affected track.
      const trackIdsArray = Array.from(plan.affectedTrackIds);
      if (trackIdsArray.length > 0) {
        refreshTracksDebounced(trackIdsArray);

        if (secondaryRefreshTimeoutRef.current)
          clearTimeout(secondaryRefreshTimeoutRef.current);
        secondaryRefreshTimeoutRef.current = setTimeout(() => {
          const visibleIds = visibleTrackIdsRef.current;
          const needsFullRefresh = trackIdsArray.some(
            (tid) => !visibleIds || visibleIds.includes(tid),
          );

          if (needsFullRefresh) {
            getTrackCars({ showSpinner: false, skipLoading: true });
          }
        }, 1000);
      }

      if (plan.leanUpserts.length > 0) {
        hydrateLeanTrackCars(
          Array.from(
            new Map(
              plan.leanUpserts
                .filter((trackCar) => trackCar?.id)
                .map((trackCar) => [trackCar.id, trackCar]),
            ).values(),
          ),
        );
      }

      if (plan.needsImmediateRefresh) {
        getTrackCars({ showSpinner: false, skipLoading: true });
      }
    },
    [
      setProcessedCarsMap,
      setTracks,
      refreshTracksDebounced,
      getTrackCars,
      hydrateLeanTrackCars,
    ],
  );

  const { addAction, flush } = useDeferredActions({
    deferCondition: isDraggingRef,
    onFlush: processQueuedUpdates,
  });

  useEffect(() => {
    if (!handleDragEndFlushRef || typeof handleDragEndFlushRef !== "object")
      return;
    handleDragEndFlushRef.current = () => flush();
  }, [handleDragEndFlushRef, flush]);

  // Use extracted subscriptions hook for cleaner subscription management
  // This hook handles all subscription setup, staleness polling, and reconnection logic
  useTrackSubscriptions({
    reloadYardId,
    useSnapshot,
    subscriptionsDisabledRef,
    subscriptionsRef,
    processedCarsMapRef,
    lastSubscriptionEventRef,
    tracks,
    setTracks,
    addAction,
    getTrackCars,
    subscriptionsEpoch,
    setSubscriptionsEpoch,
  });

  // Note: All subscription logic (setup, staleness polling, reconnection) is now handled
  // by useTrackSubscriptions hook above. The inline subscription code has been removed.

  // When the move path calls setTrackData(workingState), we must also sync processedCarsMapRef
  // so the next getTrackCars merge ({ ...ref, ...next }) does not overwrite the move with stale ref data.
  const setProcessedCarsMapWithRefSync = useCallback(
    (payload) => {
      setProcessedCarsMapAndSyncRef({
        payload,
        setProcessedCarsMap,
        processedCarsMapRef,
      });
    },
    [setProcessedCarsMap],
  );

  return {
    init,
    getTracks,
    getTrackCars,
    refreshTracksDebounced,
    refreshSpecificTracks,
    isCapacityOnlyUpdateRef,
    justCompletedDragRef,
    setProcessedCarsMapWithRefSync,
  };
};
